import moment from 'moment';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';

import { Params } from '@angular/router';
import { Injectable } from '@angular/core';

import { SummaryData } from '@misc/table-summary/table-summary.component';

import { Session } from '@services/session';
import { IResponseCompany } from '@services/company.interface';
import { StatsigService } from '@app/services/statsig.service';
import { ConfigFiltersService } from './config-filters.service';
import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';
import { AssetMonitoringFeatureFlagService } from '@app/services/feature-flags/asset-monitoring';
import { SafetyFeatureFlagService } from '@services/feature-flags/safety';
import { MaintenanceFeatureFlagService } from '@services/feature-flags/maintenance';

import { LOG_EVENT_TYPES } from '@lib/compliance-utils';
import { logger } from '@lib/datadog';

import { ITableColumn, ITableData } from '@reports/report/config-report.interface';
import { isBefore, startOfDay } from 'date-fns';

export interface IConditionalObject {
  [key: string]: (item: ITableColumn, data: ITableData, currentIndex: number, args: any) => boolean
}

export const DEFAULT_CHANGEABLE_FIELD_VALUE: string = '---';

type StatsigType = { flag: string; value: any; default: any };
type StatsigConfigType = { configKey: string; key: string; value: any; default: any };

interface IStatsigMultipleFlag {
  name: string;
  args: StatsigType | StatsigConfigType;
}

@Injectable()
export class ConfigConditionService {
  private readonly hasCompanyDriverCoachingEnabled: boolean;
  private readonly isSafetyAIRecapEnabled: boolean;

  private isNonDotMovementEnabled: boolean;
  private areCompanyDriversExempt: boolean;
  private isEventIntelligenceEnabled: boolean;
  private isVideoMonitoringEnabled: boolean;
  private isDefectLevelResolutionEnabled: boolean;
  private dataDogLogger = logger;

  private get params(): Params {
    return this.configFiltersService.queryParamsSubject.value;
  }

  constructor(
    private session: Session,
    private configFiltersService: ConfigFiltersService,
    private statsigFeatureFlagService: StatsigService,
    private complianceFeatureFlagService: ComplianceFeatureFlagService,
    private assetMonitoringFeatureFlagService: AssetMonitoringFeatureFlagService,
    private maintenanceFeatureFlagService: MaintenanceFeatureFlagService,
    private safetyFeatureFlagService: SafetyFeatureFlagService,
  ) {
    this.isNonDotMovementEnabled = complianceFeatureFlagService.isComplianceNonDotMovementEnabled();
    this.areCompanyDriversExempt = session.areCompanyDriversExempt();
    this.isEventIntelligenceEnabled = session.isEventIntelligenceEnabled();
    this.isVideoMonitoringEnabled = session.isVideoMonitoringEnabled();
    this.isDefectLevelResolutionEnabled = maintenanceFeatureFlagService.isDefectLevelResolutionEnabled();
    this.hasCompanyDriverCoachingEnabled = session.hasCompanyDriverCoachingEnabled();
    this.isSafetyAIRecapEnabled = session.isSafetyAIRecapEnabled();
  }
  /**
   * @param item The Item on which we are adding condition on
   * @param data all the data on the table
   * @param currentIndex current row index
   * @returns boolean
   *
   * Example:
   *   [ConditionName]: (item, data, currentIndex) => return [condition that we want to evaluate]
   */
  private conditionMap: IConditionalObject = {
    onNull: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return !data[currentIndex][item.key];
    },
    dataAvailable: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return !!data?.[currentIndex]?.[item.key] && data[currentIndex][item.key]?.length;
    },
    odoOffsetTripReports: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return !data[currentIndex]['noDataExists'] &&
        !data[currentIndex]['isLastRow'] &&
        data[currentIndex + 1]['trip.offset_applied'] !== data[currentIndex]['trip.offset_applied']
        ;
    },
    fuelTheftDesc: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data[currentIndex]['fuel_purchase.show_fuel_theft'];
    },
    isUnregulated: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return this.isNonDotMovementEnabled && !!data[currentIndex]?.vehicle?.isUnregulated;
    },
    dutyStatusIcon: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return (data['eld_event.duty_status'] &&
        data['eld_event.duty_status'] !== DEFAULT_CHANGEABLE_FIELD_VALUE) &&
        ((data['eld_event.duty_status'] === LOG_EVENT_TYPES.DRIVING &&
          !!data['eld_event.eld_message_offline_id']) ||
          ((data['eld_event.duty_status'] === LOG_EVENT_TYPES.OFF_DUTY ||
            data['eld_event.duty_status'] === LOG_EVENT_TYPES.ON_DUTY) &&
            !!data['eld_event.sds_code']));
    },
    isInactive: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return 'eld_event.is_active' in data[currentIndex] && !data[currentIndex]['eld_event.is_active'];
    },
    isExpired: (
      item: ITableColumn,
      data: ITableData[],
      currentIndex: number,
      { attribute }: { attribute: string }): boolean => { return data[currentIndex][attribute] <= 0; },
    attributeEquals: (
      _, data: ITableData[],
      currentIndex: number,
      { attribute, value }: { attribute: string; value: string; },
    ): boolean => data[currentIndex][attribute] === value,
    metricUnit: (_1, _2, _3, { value }: { value: boolean }) => this.session.getUserMetricUnits() === value,
    inspectionPartDefect: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['inspection_report.inspected_parts'];
    },
    defectStatus: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['inspection_report.status']
        && data[currentIndex]['inspection_report.status'] !== 'corrected'
        && data[currentIndex]['inspection_report.status'] !== 'harmless'
        && data[currentIndex]['inspection_report.inspected_parts']?.length > 0;
    },
    /** If the feature flag has been removed, log the error and fail this condition. */
    statsigFeatureFlag: (_1, _2, _3, { flag, value, default: fallback }: StatsigType) => {
      try {
        return this.statsigFeatureFlagService.evaluateGate(flag) === value;
      } catch (e) {
        this.logErrorOnDatadog(`Invalid statsig feature flag key used: ${flag} - using default value`);
        return fallback === value;
      }
    },
    /** If the dynamic config has been removed, log the error and fail this condition. */
    statsigConfig: (_1, _2, _3, { configKey, key, value, default: fallback }: StatsigConfigType) => {
      try {
        const config = this.statsigFeatureFlagService.evaluateDynamicConfig(configKey);
        return config.get(key, fallback) === value;
      } catch (e) {
        this.logErrorOnDatadog(`Invalid statsig config key used: ${configKey} - using default value`);
        return fallback === value;
      }
    },
    statsigMultipleFlagsAnd: (_1, _2, _3, { conditions }:
      { conditions: IStatsigMultipleFlag[] }): boolean => {
      return conditions.every(({ name, args }) => {
        if (name === 'statsigConfig' || name === 'statsigFeatureFlag') return this.evaluateCondition(name, _1, _2, _3, args);
      });
    },
    onDefect: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data[currentIndex]['inspected_parts']?.length > 0;
    },
    statusError: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data[currentIndex]['status'] !== 'corrected' && data[currentIndex]['status'] !== 'harmless' && data[currentIndex]['inspected_parts']?.length > 0;
    },
    companyProperty: (_1, _2, _3, {property, value}: { property: string; value: any; }) => {
      return this.session.getCompanyProperty(property as keyof IResponseCompany) === value;
    },
    isCurrentCycle: (_1: ITableColumn, _2: ITableData[], _3: number, { condition }: { condition: boolean }): boolean => {
      // Adding temporary flag check, it will be removed after the GA of Wifi hotspot UI changes.
      if (!this.assetMonitoringFeatureFlagService.isAGWifiHotspotReportChangesEnabled()) return true;
      const isCurrentCycle: boolean = isNil(this.params?.end_date) || moment().diff(moment(this.params?.end_date), 'd') <= 0;
      return isCurrentCycle === condition;
    },
    companyDriversNotExempt: (): boolean => !this.areCompanyDriversExempt,
    firmwareVersion: (item: ITableColumn, data: ITableData[], currentIndex: number) => {
      return data?.[currentIndex]?.['current_sw_pkg_version'] !== data?.[currentIndex]?.['target_sw_pkg_version'];
    },
    assetGatewayFirmwareVersion: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['asset_gateway.current_sw_pkg_version'] !== data?.[currentIndex]?.['asset_gateway.target_sw_pkg_version'];
    },
    deviceHealthFirmwareVersion: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['device.firmware'] !== data?.[currentIndex]?.['device.target_firmware'];
    },
    deviceHealthSuccessStatus: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['device.health_status'] === 'Normal';
    },
    deviceHealthErrorStatus: (item: ITableColumn, data: ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['device.health_status'] === 'Pending return' || data?.[currentIndex]?.['device.status'] === 'Check install';
    },
    historicalProcessingInProgress: (item: ITableColumn, data: SummaryData | ITableData[], currentIndex: number): boolean => {
      return data?.[currentIndex]?.['geofence.historical_processing_in_progress'] || data?.['historical_processing_in_progress'];
    },
    isEventIntelligenceEnabled: (): boolean => {
      return this.isEventIntelligenceEnabled;
    },
    isEventIntelligenceDisabled: (): boolean => {
      return !this.isEventIntelligenceEnabled;
    },
    isVideoMonitoringEnabled: (): boolean => {
      return this.isVideoMonitoringEnabled;
    },
    isDefectLevelResolutionEnabled: (): boolean => {
      return this.isDefectLevelResolutionEnabled;
    },
    isDefectLevelResolutionDisabled: (): boolean => {
      return !this.isDefectLevelResolutionEnabled;
    },
    isDriverCoachingAndCoachableNotificationsEnabled: (): boolean => {
      // remove 1 release after release/webapp-1.120 live
      return this.hasCompanyDriverCoachingEnabled;
    },
    hasCompanyDriverCoachingEnabled: (): boolean => {
      return this.hasCompanyDriverCoachingEnabled;
    },
    isSafetyAIRecapEnabled: (): boolean => {
      return this.isSafetyAIRecapEnabled;
    },
    isSpeedingAndSafetyScoreEnabled: (): boolean => {
      return this.session.isSpeedingEnabled() && !this.session.isSafetyScoreHidden();
    },
    isAUXConnectionEnabled: (item: ITableColumn, data: SummaryData | ITableData[], currentIndex: number, args): boolean => {
      return this.session.isAuxEnabled();
    },
    isApproximateIdleFuel: (item: ITableColumn, data: SummaryData | ITableData[], currentIndex: number): boolean => {
      return data?.['idle_event.is_approximate_idle_fuel'];
    },
    isApproximateIdleFuelSummary: (item: ITableColumn, data: SummaryData | ITableData[], currentIndex: number): boolean => {
      return data?.['is_approximate_idle_fuel'];
    },
    isConfidentialDisplayEnabled: (): boolean => {
      const hasConfidentialReadPermission: boolean = this.session.userPermissions.isConfidentialReadAllowed();
      return this.safetyFeatureFlagService.isConfidentialEventsEnabled() && hasConfidentialReadPermission;
    },
    isPastDate: (item: ITableColumn, data: SummaryData | ITableData[], currentIndex: number, args): boolean => {
      const date = data?.[currentIndex]?.[item.key];
      if (!date) return false;
      return isBefore(startOfDay(new Date(date)), startOfDay(new Date()));
    }
  };

  public evaluateCondition(
    name: string,
    item: ITableColumn,
    data: ITableData[] | SummaryData,
    currentIndex: number,
    args: { [key: string]: any; } = {},
  ): boolean {
    if (!this.conditionMap[name]) {
      this.logErrorOnDatadog(`Unrecognized condition in table config evaluation: ${name}`);
      return false;
    }
    return this.conditionMap[name](item, data, currentIndex, args);
  }

  public logErrorOnDatadog(msg) {
    this.dataDogLogger?.error(msg);
  }
}